import type { MapPoint } from "./directoryMapData";

export type RoutePoint = { lat: number; lng: number };

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Distancia en línea recta (no la ruta real de calles) entre dos coordenadas. */
export const haversineKm = (a: RoutePoint, b: RoutePoint): number => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

const isValidPoint = (
  p: MapPoint,
): p is MapPoint & { lat: number; lng: number } =>
  typeof p.lat === "number" &&
  typeof p.lng === "number" &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 &&
  Math.abs(p.lng) <= 180;

export type OrderedStop = MapPoint & {
  lat: number;
  lng: number;
  /** distancia en km desde la parada anterior (o el punto de partida en la primera) */
  legKm: number;
};

/**
 * Orden por vecino más cercano (greedy, O(n²) — suficiente para ≤25 paradas) desde
 * `start`. Descarta puntos sin lat/lng válidas, igual que `isValidPoint` en
 * directoryMapData.ts. En empates de distancia, conserva el primero encontrado en
 * el arreglo de entrada (orden determinista).
 * ponytail: no es la ruta óptima real de calles, solo línea recta — Google Maps ya
 * optimiza el tramo real al abrir el enlace. Si el límite de paradas crece mucho,
 * mover esto a backend/PostGIS.
 */
export const orderByNearestNeighbor = (
  start: RoutePoint,
  points: MapPoint[],
): OrderedStop[] => {
  const remaining = points.filter(isValidPoint);
  const ordered: OrderedStop[] = [];
  let current: RoutePoint = start;

  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestKm = haversineKm(current, remaining[0]);
    for (let i = 1; i < remaining.length; i++) {
      const km = haversineKm(current, remaining[i]);
      if (km < nearestKm) {
        nearestKm = km;
        nearestIdx = i;
      }
    }
    const [next] = remaining.splice(nearestIdx, 1);
    ordered.push({ ...next, legKm: nearestKm });
    current = next;
  }

  return ordered;
};

export type ValidPoint = MapPoint & { lat: number; lng: number };

/**
 * Recalcula `legKm` para un orden YA FIJO (no reordena) — se usa tras mover o
 * quitar paradas a mano, para que la distancia de cada tramo siga reflejando
 * a su vecino real en la lista.
 */
export const recomputeLegs = (
  start: RoutePoint,
  points: ValidPoint[],
): OrderedStop[] => {
  let current: RoutePoint = start;
  return points.map((p) => {
    const legKm = haversineKm(current, p);
    current = p;
    return { ...p, legKm };
  });
};

/** Umbral (km) a partir del cual un tramo sugiere ir en coche en vez de caminando. */
export const LONG_LEG_KM = 1.5;

/** Marca cada parada cuyo tramo de llegada supera el umbral. */
export const markLongLegs = <T extends { legKm: number }>(
  stops: T[],
  thresholdKm = LONG_LEG_KM,
): (T & { longLeg: boolean })[] =>
  stops.map((s) => ({ ...s, longLeg: s.legKm > thresholdKm }));
